import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Heart, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface TeamFollowButtonProps {
  teamId: number;
  userId?: string;
  userType?: 'supabase' | 'internal';
  size?: 'sm' | 'default' | 'lg';
  showCount?: boolean;
  className?: string;
}

export const TeamFollowButton = ({
  teamId,
  userId,
  userType = 'supabase',
  size = 'default',
  showCount = true,
  className
}: TeamFollowButtonProps) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadFollowStatus();
    loadFollowerCount();
  }, [teamId, userId]);

  const loadFollowStatus = async () => {
    if (!userId) return;

    try {
      const { data, error } = await supabase
        .from('fan_teams')
        .select('*')
        .eq('team_id', teamId)
        .eq('fan_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error checking follow status:', error);
        return;
      }

      setIsFollowing(!!data);
    } catch (error) {
      console.error('Error loading follow status:', error);
    }
  };

  const loadFollowerCount = async () => {
    try {
      const { count, error } = await supabase
        .from('fan_teams')
        .select('*', { count: 'exact', head: true })
        .eq('team_id', teamId);

      if (error) {
        console.error('Error loading follower count:', error);
        return;
      }

      setFollowerCount(count || 0);
    } catch (error) {
      console.error('Error loading follower count:', error);
    }
  };

  const handleFollowToggle = async () => {
    if (!userId) {
      toast.error('Please sign in to follow teams');
      return;
    }

    setLoading(true);

    try {
      if (isFollowing) {
        // Unfollow
        const { error } = await supabase
          .from('fan_teams')
          .delete()
          .eq('team_id', teamId)
          .eq('fan_id', userId);

        if (error) throw error;

        setIsFollowing(false);
        setFollowerCount(prev => Math.max(0, prev - 1));
        toast.success('Unfollowed team');
      } else {
        // Follow
        const { error } = await supabase
          .from('fan_teams')
          .insert({
            team_id: teamId,
            fan_id: userId,
            follower_type: userType
          });

        if (error) throw error;

        setIsFollowing(true);
        setFollowerCount(prev => prev + 1);
        toast.success('Following team!');
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      toast.error('Failed to update follow status');
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'h-8 px-3 text-xs',
    default: 'h-10 px-4 text-sm',
    lg: 'h-12 px-6 text-base'
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Button
        onClick={handleFollowToggle}
        disabled={loading || !userId}
        variant={isFollowing ? 'default' : 'outline'}
        className={cn(
          sizeClasses[size],
          isFollowing 
            ? 'bg-red-500 hover:bg-red-600 text-white border-red-500' 
            : 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white'
        )}
      >
        <Heart 
          className={cn(
            'mr-2',
            size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4',
            isFollowing && 'fill-current'
          )} 
        />
        {isFollowing ? 'Following' : 'Follow'}
      </Button>
      
      {showCount && (
        <div className="flex items-center gap-1 text-gray-400 text-sm">
          <Users className="h-4 w-4" />
          <span>{followerCount}</span>
        </div>
      )}
    </div>
  );
};
